import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { LMX_PAPER_URL, NEURO_SAN_URL } from "../constants";
import { landingPalette } from "../theme";
// #region: Styled Components
const ActionButton = styled("button")({
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    alignSelf: "flex-start",
    padding: "12px 20px",
    border: "none",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: "0.95rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
});
// The outlined variant supplies the paper background and divider border from the theme.
const Card = styled(MuiCard)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 32,
    borderRadius: 12,
    boxShadow: theme.shadows[1],
    cursor: "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    ":hover": {
        borderColor: landingPalette.accent,
        boxShadow: theme.shadows[6],
    },
}));
const CardDescription = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    marginBottom: 8,
}));
const CardGrid = styled(Box)({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 24,
    marginBottom: 48,
    "@media (max-width: 900px)": {
        gridTemplateColumns: "1fr",
    },
});
// styled() erases Typography's polymorphic component prop, so it is redeclared here.
const CardTitle = styled(Typography)({
    fontSize: "1.5rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: landingPalette.headline,
});
const DarkActionButton = styled(ActionButton)(({ theme }) => ({
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
        background: landingPalette.buttonHover,
        boxShadow: landingPalette.buttonHoverShadow,
    },
}));
const Eyebrow = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: 12,
}));
const GreenActionButton = styled(ActionButton)(({ theme }) => ({
    background: landingPalette.accent,
    color: theme.palette.primary.main,
    "&:hover": {
        background: landingPalette.accentHover,
    },
}));
const Headline = styled(Typography)({
    marginBottom: 20,
    fontSize: "2.75rem",
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: "-0.02em",
    color: landingPalette.headline,
});
const LandingContainer = styled(Box)({
    maxWidth: 1100,
    margin: "0 auto",
    padding: "48px 0 64px",
});
const LearnMoreLinks = styled(Box)({
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
});
const LinkChip = styled("a")(({ theme }) => ({
    padding: "10px 16px",
    borderRadius: 8,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: landingPalette.headline,
    fontSize: "0.95rem",
    fontWeight: 500,
    textDecoration: "none",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
        borderColor: landingPalette.accent,
        background: landingPalette.accentTint,
    },
}));
const Separator = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    marginBottom: 32,
}));
const Subhead = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: "1.1rem",
    marginBottom: 40,
}));
// #endregion: Types
const LandingPage = ({ onStartFromScratch, onBrowseExamples }) => (_jsxs(LandingContainer, { children: [_jsx(Headline, { component: "h1", children: "Think through a decision with AI in the room." }), _jsx(Subhead, { children: "Bring your own question, or let LMX suggest one. Either way you'll end up with two objectives, a set of options, and a reasoned trade-off." }), _jsxs(CardGrid, { children: [_jsxs(Card, { onClick: onStartFromScratch, variant: "outlined", children: [_jsx(CardTitle, { component: "h2", children: "I have my own idea" }), _jsx(CardDescription, { children: "Describe the decision you're facing and the two things you're weighing." }), _jsxs(GreenActionButton, { type: "button", children: ["Start from scratch ", _jsx("span", { "aria-hidden": "true", children: "\u2192" })] })] }), _jsxs(Card, { onClick: onBrowseExamples, variant: "outlined", children: [_jsx(CardTitle, { component: "h2", children: "Guide me" }), _jsx(CardDescription, { children: "Browse ready-made starting points across industries and everyday choices." }), _jsxs(DarkActionButton, { type: "button", children: ["Suggest ideas ", _jsx("span", { "aria-hidden": "true", children: "\u2192" })] })] })] }), _jsx(Separator, {}), _jsx(Eyebrow, { children: "Learn more" }), _jsxs(LearnMoreLinks, { children: [_jsxs(LinkChip, { href: NEURO_SAN_URL, rel: "noopener noreferrer", target: "_blank", children: ["Powered by Cognizant Neuro San ", _jsx("span", { "aria-hidden": "true", children: "\u2197" })] }), _jsxs(LinkChip, { href: LMX_PAPER_URL, rel: "noopener noreferrer", target: "_blank", children: ["How LMX works ", _jsx("span", { "aria-hidden": "true", children: "\u2197" })] })] })] }));
export default LandingPage;
